import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

const FONT_SIZES = ["sm", "md", "lg", "xl"];
const FONT_ROOT_PX = { sm: 14, md: 16, lg: 18, xl: 20 };

function applyFontSize(size) {
  const px = FONT_ROOT_PX[size] || 16;
  document.documentElement.style.fontSize = `${px}px`;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [branch, setBranchState] = useState(() => {
    try {
      const raw = localStorage.getItem("badaris_branch");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [fontSize, setFontSizeState] = useState("md");

  const setBranch = useCallback((b) => {
    if (b) {
      localStorage.setItem("badaris_branch", JSON.stringify(b));
    } else {
      localStorage.removeItem("badaris_branch");
    }
    setBranchState(b);
  }, []);

  const setFontSize = useCallback((size, username) => {
    const s = FONT_SIZES.includes(size) ? size : "md";
    const key = username ? `badaris_font_${username}` : "badaris_font";
    localStorage.setItem(key, s);
    applyFontSize(s);
    setFontSizeState(s);
  }, []);

  const loadUserFontPref = useCallback((u) => {
    const s = (u && localStorage.getItem(`badaris_font_${u.username}`)) || "md";
    applyFontSize(FONT_SIZES.includes(s) ? s : "md");
    setFontSizeState(s);
  }, []);

  const bootstrap = useCallback(async () => {
    const token = localStorage.getItem("badaris_token");
    if (!token) {
      applyFontSize("md");
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      loadUserFontPref(data);
    } catch {
      localStorage.removeItem("badaris_token");
      setUser(false);
    }
  }, [loadUserFontPref]);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = useCallback(async (username, password) => {
    const { data } = await api.post("/auth/login", { username, password });
    localStorage.setItem("badaris_token", data.token);
    setUser(data.user);
    loadUserFontPref(data.user);
    return data.user;
  }, [loadUserFontPref]);

  const logout = useCallback(() => {
    localStorage.removeItem("badaris_token");
    localStorage.removeItem("badaris_branch");
    applyFontSize("md");
    setUser(false);
    setBranchState(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, branch, setBranch, login, logout, fontSize, setFontSize, FONT_SIZES }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
